import { Button } from 'designSystem/components/Button'
import { Text } from 'designSystem/components/Text'
import { X } from 'lucide-react'
import type { FC, ReactNode } from 'react'
import styled from 'styled-components'

import { HoverTip } from './HoverTip'

const Wrap = styled.div<{ $scrollWithParent: boolean }>`
  display: flex;
  flex-direction: column;
  flex: ${({ $scrollWithParent }) =>
    $scrollWithParent ? '0 0 auto' : '0 1 auto'};
  align-self: stretch;
  min-height: 0;
  /* Leave a floor of space for document metadata below. */
  max-height: ${({ $scrollWithParent }) =>
    $scrollWithParent ? 'none' : 'calc(100% - 12rem)'};
  overflow-x: ${({ $scrollWithParent }) =>
    $scrollWithParent ? 'visible' : 'hidden'};
  overflow-y: ${({ $scrollWithParent }) =>
    $scrollWithParent ? 'visible' : 'auto'};
  margin: 0.5rem;
  border: 0.0625rem solid var(--border);
  border-radius: 0.5rem;
  background-color: var(--muted);
  color: var(--foreground);
  font-size: 0.75rem;
`

/** Padding lives here so overflow scrolling does not clip the bottom inset. */
const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.75rem;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`

const HeaderText = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  flex-shrink: 0;
`

interface EntityPanelProps {
  scrollWithParent?: boolean
  eyebrow?: string
  title: string | undefined
  actions: ReactNode
  closeLabel: string
  onClose: () => void
  children: ReactNode
}

export const EntityPanel: FC<EntityPanelProps> = ({
  scrollWithParent = false,
  eyebrow,
  title,
  actions,
  closeLabel,
  onClose,
  children,
}) => (
  <Wrap $scrollWithParent={scrollWithParent}>
    <PanelBody>
      <Header>
        <HeaderText>
          {eyebrow && (
            <Text as='div' variant='caption' color='muted'>
              {eyebrow}
            </Text>
          )}
          <Text as='div' size='sm' weight='semibold' truncate title={title}>
            {title ?? eyebrow}
          </Text>
        </HeaderText>
        <HeaderActions>
          {actions}
          <HoverTip tip={closeLabel}>
            <Button
              type='button'
              variant='outline'
              size='icon'
              onClick={onClose}
              aria-label={closeLabel}>
              <X />
            </Button>
          </HoverTip>
        </HeaderActions>
      </Header>
      {children}
    </PanelBody>
  </Wrap>
)
